/**
 * Outils › Trousseau (outils.trousseau, 29/10/2026) – règles partagées API / front du gestionnaire de mots de passe :
 * trousseau personnel, trousseaux de service (membres, demandes validées par le responsable), trousseaux rattachés à un
 * objet (trousseau d'un site). Secrets chiffrés côté API (enveloppe KMS), récupérables par l'administrateur AGORA
 * (motif, vérification d'identité, trace). Détail : docs/TROUSSEAU.md.
 */

export const RESSOURCE_TROUSSEAU = 'outils.trousseau';
export const ROUTE_TROUSSEAU = '/outils/trousseau';

/** personnel : un par utilisateur ; service : partagé entre ses membres ; objet : rattaché à un objet (site…). */
export const NATURES_TROUSSEAU = ['personnel', 'service', 'objet'] as const;
export type NatureTrousseau = (typeof NATURES_TROUSSEAU)[number];

/** Rôles dans un trousseau de service : responsable et suppléant écrivent et valident, un membre lit et demande. */
export const ROLES_TROUSSEAU = ['responsable', 'suppleant', 'membre'] as const;
export type RoleTrousseau = (typeof ROLES_TROUSSEAU)[number];
export const ROLES_ECRITURE: readonly RoleTrousseau[] = ['responsable', 'suppleant'];

/** Sorte d'entrée : site web (URL), application de bureau (serveur, poste), équipement (automate, adresse IP). */
export const TYPES_ENTREE_TROUSSEAU = ['web', 'application', 'equipement'] as const;
export type TypeEntreeTrousseau = (typeof TYPES_ENTREE_TROUSSEAU)[number];

export const TYPES_DEMANDE_TROUSSEAU = ['renouvellement', 'ajout'] as const;
export type TypeDemandeTrousseau = (typeof TYPES_DEMANDE_TROUSSEAU)[number];
export const STATUTS_DEMANDE_TROUSSEAU = ['en-attente', 'validee', 'refusee', 'annulee'] as const;
export type StatutDemandeTrousseau = (typeof STATUTS_DEMANDE_TROUSSEAU)[number];

export const ORIGINES_VERSION_TROUSSEAU = ['creation', 'modification', 'demande', 'transfert', 'restauration'] as const;
export type OrigineVersionTrousseau = (typeof ORIGINES_VERSION_TROUSSEAU)[number];

/** Entrée personnelle supprimée : gardée à la corbeille ce nombre de jours, puis purgée (secret effacé, anonymisée). */
export const DELAI_CORBEILLE_PERSONNELLE_JOURS = 30;
/** Départ constaté (compte AGORA désactivé : supprimé ou désactivé dans Entra) : trousseau personnel purgé après. */
export const DELAI_DEPART_JOURS = 30;

/** Mot de passe affiché : masqué de nouveau après ce délai. */
export const DUREE_AFFICHAGE_S = 30;
/** Mot de passe copié : presse-papiers vidé après ce délai (au mieux : le navigateur doit avoir le focus). */
export const DUREE_PRESSE_PAPIERS_S = 90;

/** Garde-fou contre l'extraction en masse : au-delà, refus (trousseau_limite) et alerte dans le journal. */
export const LIMITE_AFFICHAGES = { nombre: 30, minutes: 10 } as const;

/** Bornes de saisie (caractères). */
export const BORNES_TROUSSEAU = {
  nom: 200,
  adresse: 2000,
  identifiant: 320,
  motDePasse: 1000,
  note: 10_000,
  commentaire: 1000,
  nomTrousseau: 120,
  description: 1000,
  motif: 500,
} as const;

/** Générateur de mots de passe (formulaire) : longueur par défaut et bornes. */
export const GENERATEUR_MOT_DE_PASSE = { defaut: 20, min: 12, max: 64 } as const;

/** Libellé d'une entrée purgée (champs en clair anonymisés). */
export const NOM_ENTREE_PURGEE = '—';

/**
 * Groupe Entra de RÉCUPÉRATION (décision T2 du 29/10/2026 ; membres : docs/DROITS.md) : seul ce groupe, en plus du droit
 * « administrer » sur l'outil, ouvre le trousseau personnel d'un autre (motif, vérification d'identité, trace,
 * propriétaire notifié). Sans membre, personne ne récupère.
 */
export const GROUPE_RECUPERATION_TROUSSEAU = 'AGORA-GRP-TROUSSEAU-ADMIN';

/**
 * Trousseaux rattachés à un objet (lot 4) : clé du registre (api/src/outils/trousseau/objets.ts) → champ sensible Cedar qui
 * porte les droits. Identifiant d'objet : lettres, chiffres, point, tiret, souligné (jamais « : », séparateur du
 * paramètre de session).
 */
export const OBJETS_TROUSSEAU = { sites: 'sites.trousseau' } as const;
export type RessourceObjetTrousseau = keyof typeof OBJETS_TROUSSEAU;
export const MOTIF_ID_OBJET = /^[A-Za-z0-9._-]{1,100}$/;

/** Le rôle permet-il d'écrire (créer, modifier, archiver, valider une demande) ? */
export const roleEcrit = (role: RoleTrousseau | null | undefined): boolean => !!role && ROLES_ECRITURE.includes(role);
